import React, { useMemo, useRef, useState } from "react";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import { TextInput } from "components/common/TextInput";
import { DropdownMenu } from "components/common/DropdownMenu";
import { DropdownMenuItem } from "components/common/DropdownMenuItem";
import { SectionRule } from "components/common/SectionRule";
import { Callout } from "components/common/Callout";
import { Tooltip } from "components/common/Tooltip";
import { TooltipButton } from "components/common/TooltipButton";
import { Badge } from "components/common/Badge";
import { Dialog } from "@/components/main/utils/Dialog";
import { DialogActions } from "@/components/main/utils/DialogActions";
import { showNotificationGlobal } from "stores/useNotificationStore";
import { validateEmail } from "components/shareSection/entities";
import { RefreshableCount } from "@/components/common/RefreshableCount";
import { SeatLimitNotice } from "components/main/organizations/SeatLimitNotice";
import { InlineSpinner } from "components/common/InlineSpinner";
import {
  addOrgMember,
  fetchRemoveMemberPreview,
  removeOrgMember,
  SeatLimitError,
  setOrgAdmin,
  setOrgOwner,
  type LeaveOrgPreview,
  type Org,
  type OrgMember,
} from "@/lib/api/organizations";

const message = (error: unknown, fallback: string) =>
  error instanceof Error ? error.message : fallback;

/// After a seat purchase the invite door still reads the old cap until the
/// webhook lands, so the refused invitation is retried for a while.
const SEAT_SETTLE_ATTEMPTS = 6;
const SEAT_SETTLE_INTERVAL_MS = 1500;

const roleLabel = (role: string) =>
  role === "org_admin" ? "Admin" : role === "owner" ? "Owner" : "Member";

export const MembersPanel: React.FC<{
  org: Org;
  members: OrgMember[];
  onChanged: () => void | Promise<void>;
}> = ({ org, members, onChanged }) => {
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteError, setInviteError] = useState<string | null>(null);
  /// The seat-cap refusal, with the address to re-invite once a seat exists.
  const [seatWall, setSeatWall] = useState<{
    used: number;
    limit: number;
    email: string;
  } | null>(null);
  /// The address whose invitation is being retried after a seat purchase.
  const [settlingSeat, setSettlingSeat] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const trimmedInvite = inviteEmail.trim();
  // A scheduled org takes no new members — the server refuses, and an enabled
  // field would only collect the refusal.
  const scheduledForDeletion = !!org.deletion_scheduled_at;
  const inviteReady = validateEmail(trimmedInvite) && !scheduledForDeletion;

  const pendingCount = useMemo(
    () => members.filter((m) => m.pending).length,
    [members],
  );
  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return members;
    return members.filter(
      (m) =>
        m.display_name.toLowerCase().includes(needle) ||
        m.email.toLowerCase().includes(needle),
    );
  }, [members, search]);

  /// `settleAttempts` retries a seat-cap refusal while the webhook lands.
  const sendInvite = async (email: string, settleAttempts = 0) => {
    try {
      const status = await addOrgMember(org.org_id, email);
      setInviteEmail("");
      setSeatWall(null);
      setSettlingSeat(null);
      onChanged();
      showNotificationGlobal(
        status === "already_member"
          ? `${email} is already a member`
          : `Invitation sent to ${email}`,
        "success",
      );
    } catch (error) {
      if (error instanceof SeatLimitError) {
        if (settleAttempts > 0) {
          await new Promise((resolve) =>
            setTimeout(resolve, SEAT_SETTLE_INTERVAL_MS),
          );
          return sendInvite(email, settleAttempts - 1);
        }
        setSettlingSeat(null);
        setSeatWall({ used: error.used, limit: error.limit, email });
        return;
      }
      setSettlingSeat(null);
      showNotificationGlobal(message(error, "Invite failed"), "error");
    }
  };

  const submitInvite = async () => {
    const email = trimmedInvite;
    if (!email) return;
    // Caught here: Enter fires on every keystroke pass, so a half-typed address
    // would round-trip as a refusal about text the field no longer holds.
    if (!inviteReady) {
      setInviteError(`"${email}" is not a valid email address`);
      return;
    }
    setInviteError(null);
    await sendInvite(email);
  };

  /// Owner is a separate door from admin, and moving between the two means
  /// clearing the one you are leaving.
  const changeRole = async (member: OrgMember, next: OrgRole) => {
    try {
      if (member.role === "owner" && next !== "owner") {
        await setOrgOwner(org.org_id, member.client_id, false);
      }
      if (next === "owner") {
        await setOrgOwner(org.org_id, member.client_id, true);
      } else {
        await setOrgAdmin(org.org_id, member.client_id, next === "org_admin");
      }
      onChanged();
      showNotificationGlobal(
        `${member.display_name || member.email} is now ${roleLabel(next)}`,
        "success",
      );
    } catch (error) {
      showNotificationGlobal(message(error, "Role change failed"), "error");
    }
  };

  // Removal can reap the member's empty spaces and revoke their grants, so
  // the confirm names both before it happens (D43).
  const [removing, setRemoving] = useState<OrgMember | null>(null);
  /// An owner is removed by an owner, while another owner remains.
  const ownerCount = members.filter(
    (m) => m.role === "owner" && !m.pending,
  ).length;
  // Roles are `can_manage_admins`, owner-only; an admin manages members, not roles.
  const roleChangeBlock =
    org.role === "owner" ? null : "Only an owner can change roles";
  const removeBlockFor = (member: OrgMember): string | null => {
    if (member.role !== "owner") return null;
    if (ownerCount <= 1) return "Transfer ownership first";
    if (org.role !== "owner") return "Only an owner can remove an owner";
    return null;
  };

  const [removePreview, setRemovePreview] = useState<LeaveOrgPreview | null>(
    null,
  );

  const openRemove = async (member: OrgMember) => {
    setRemovePreview(null);
    setRemoving(member);
    try {
      setRemovePreview(
        await fetchRemoveMemberPreview(org.org_id, member.client_id),
      );
    } catch (error) {
      setRemoving(null);
      showNotificationGlobal(message(error, "Could not check"), "error");
    }
  };

  const confirmRemove = async () => {
    if (!removing) return;
    const label = removing.display_name || removing.email;
    try {
      await removeOrgMember(org.org_id, removing.client_id);
      setRemoving(null);
      onChanged();
      showNotificationGlobal(`Removed ${label}`, "success");
    } catch (error) {
      showNotificationGlobal(message(error, "Removal failed"), "error");
    }
  };

  const cancelInvite = async (member: OrgMember) => {
    try {
      await removeOrgMember(org.org_id, member.client_id, true);
      await onChanged();
      showNotificationGlobal(
        `Invitation to ${member.email} cancelled`,
        "success",
      );
    } catch (error) {
      // Most likely they accepted while this roster sat open, so show where
      // they actually stand rather than leaving the stale row on screen.
      await onChanged();
      showNotificationGlobal(message(error, "Could not cancel"), "error");
    }
  };

  return (
    <>
      <section className="flex flex-col">
        <SectionRule label="Invite" />
        <div className="flex items-center gap-2">
          <TextInput
            value={inviteEmail}
            onChange={(value) => {
              setInviteEmail(value);
              if (inviteError) setInviteError(null);
              if (seatWall) setSeatWall(null);
            }}
            ariaInvalid={inviteError !== null}
            disabled={scheduledForDeletion}
            placeholder="name@company.com"
            ariaLabel="Invite by email"
            autoComplete="off"
            name="org-invite"
            onKeyDown={(e) => {
              if (e.key === "Enter") void submitInvite();
            }}
          />
          <TooltipButton
            title={
              scheduledForDeletion
                ? "This organization is scheduled for deletion — cancel it to invite anyone."
                : "They join once they accept."
            }
            variant="primary"
            size="md"
            onClick={() => void submitInvite()}
            disabled={!inviteReady}
          >
            Invite
          </TooltipButton>
        </div>
        {seatWall && (
          <div className="pt-2">
            <SeatLimitNotice
              orgId={org.org_id}
              used={seatWall.used}
              limit={seatWall.limit}
              email={seatWall.email}
              onSeatsAdded={() => {
                const { email } = seatWall;
                setSeatWall(null);
                setSettlingSeat(email);
                return sendInvite(email, SEAT_SETTLE_ATTEMPTS);
              }}
            />
          </div>
        )}
        {settlingSeat && (
          <span
            className="flex items-center gap-2 px-2 pt-2 text-fg-muted-regular-small"
            data-testid="seat-settling"
          >
            <InlineSpinner size={14} />
            {`Seats added. Sending the invitation to ${settlingSeat}…`}
          </span>
        )}
        {inviteError ? (
          <span className="px-2 pt-1 text-error">{inviteError}</span>
        ) : (
          <span className="px-2 pt-1 text-fg-muted-regular-small">
            {scheduledForDeletion
              ? "This organization is scheduled for deletion and takes no new members. Cancel the deletion in General first."
              : "Membership begins when the invitation is accepted. Admins govern the organization; reading content requires a grant on a space."}
          </span>
        )}
      </section>

      <section className="flex flex-col">
        <SectionRule
          label="Members"
          trailing={
            <RefreshableCount
              label={`${members.length}${
                pendingCount > 0 ? ` · ${pendingCount} pending` : ""
              }`}
              subject="members"
              onRefresh={onChanged}
            />
          }
        />
        {members.length > 8 && (
          <div className="px-2 pb-2">
            <TextInput
              value={search}
              onChange={setSearch}
              placeholder="Find someone…"
              ariaLabel="Find a member"
            />
          </div>
        )}
        <div className="flex max-h-96 flex-col overflow-y-auto">
          {visible.map((member) => (
            <MemberRow
              key={member.client_id}
              member={member}
              roleChangeBlock={roleChangeBlock}
              removeBlock={removeBlockFor(member)}
              onRoleChange={(next) => void changeRole(member, next)}
              onRemove={() =>
                member.pending
                  ? void cancelInvite(member)
                  : void openRemove(member)
              }
            />
          ))}
          {visible.length === 0 && (
            <span className="px-2 py-2 text-fg-muted-regular-small">
              {search ? "No members match." : "No members yet."}
            </span>
          )}
        </div>
      </section>

      <Dialog
        open={removing !== null}
        title={`Remove ${(removing?.display_name || removing?.email) ?? ""}?`}
        onClose={() => setRemoving(null)}
      >
        {removePreview === null ? (
          <span className="text-fg-muted-regular">
            Checking the effects of removal…
          </span>
        ) : (
          <div className="flex flex-col gap-2">
            {removePreview.is_owner_blocked ? (
              <span className="text-fg-regular">
                They are this organization&apos;s only owner. Make someone else
                an owner first.
              </span>
            ) : (
              <>
                <span className="text-fg-regular">
                  They will lose access to all content in this organization.
                </span>
                {removePreview.pays_for_org && (
                  <Callout
                    variant="warning"
                    title="This organization's subscription is on their card"
                  >
                    Removing them does not move it: it keeps charging them until
                    you replace the card in Plan &amp; usage.
                  </Callout>
                )}
                {removePreview.spaces_to_orphan.length > 0 && (
                  <Callout
                    variant="warning"
                    title="These spaces lose their manager"
                  >
                    <span className="flex flex-col gap-1">
                      <span className="text-fg-medium">
                        {removePreview.spaces_to_orphan.join(", ")}
                      </span>
                      They are the only manager. Nobody will own the models
                      inside until someone is appointed — Spaces lists them as
                      orphaned.
                    </span>
                  </Callout>
                )}
                {removePreview.spaces_to_delete.length > 0 && (
                  <span className="text-fg-regular">
                    Their empty{" "}
                    {removePreview.spaces_to_delete.length === 1
                      ? "space"
                      : "spaces"}{" "}
                    <span className="text-fg-medium">
                      {removePreview.spaces_to_delete.join(", ")}
                    </span>{" "}
                    will be deleted.
                  </span>
                )}
                {removePreview.grants_to_revoke.length > 0 && (
                  <span className="text-fg-regular">
                    Their access to{" "}
                    <span className="text-fg-medium">
                      {removePreview.grants_to_revoke.join(", ")}
                    </span>{" "}
                    will be revoked; other members keep theirs.
                  </span>
                )}
              </>
            )}
          </div>
        )}
        <DialogActions
          actions={[
            {
              label: "Cancel",
              variant: "secondary",
              onClick: () => setRemoving(null),
            },
            {
              label: "Remove from organization",
              variant: "primary",
              disabled:
                removePreview === null || removePreview.is_owner_blocked,
              onClick: () => void confirmRemove(),
            },
          ]}
        />
      </Dialog>
    </>
  );
};

type OrgRole = "member" | "org_admin" | "owner";

const MemberRow: React.FC<{
  member: OrgMember;
  /// Why this viewer cannot change roles, or `null` when they can.
  roleChangeBlock: string | null;
  /// Why this member cannot be removed from here, or `null` when they can.
  removeBlock: string | null;
  onRoleChange: (next: OrgRole) => void;
  onRemove: () => void;
}> = ({ member, roleChangeBlock, removeBlock, onRoleChange, onRemove }) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const isSelf = member.client_id === localStorage.getItem("clientID");
  const isOwner = member.role === "owner";
  const isAdmin = member.role === "org_admin";

  // Owner is a set, not a slot — an org may hold several. The server refuses
  // to remove the last one, so demotion stays offered.
  const roleItems = (
    [
      {
        label: "Member",
        role: "member",
        selected: !isAdmin && !isOwner,
      },
      { label: "Admin", role: "org_admin", selected: isAdmin },
      { label: "Owner", role: "owner", selected: isOwner },
    ] as { label: string; role: OrgRole; selected: boolean }[]
  ).map((item) => (
    <DropdownMenuItem
      key={item.label}
      isSelected={item.selected}
      disabled={roleChangeBlock !== null}
      onClick={() => {
        setOpen(false);
        if (item.selected) return;
        onRoleChange(item.role);
      }}
    >
      <span className="flex-1 text-left text-fg-regular">{item.label}</span>
      {item.label === "Admin" && (
        <span className="text-fg-muted-regular-small">
          governs, cannot read
        </span>
      )}
      {item.label === "Owner" && (
        <span className="text-fg-muted-regular-small">full control</span>
      )}
      {item.selected && (
        <CheckIcon sx={{ fontSize: 12 }} className="text-accent" />
      )}
    </DropdownMenuItem>
  ));

  return (
    <div className="group flex h-8 items-center gap-2 rounded-app px-2 hover:bg-list-hover">
      <span className="flex min-w-0 flex-1 items-center gap-2">
        <span className="min-w-0 truncate">
          <span
            className={
              member.pending ? "text-fg-muted-regular" : "text-fg-regular"
            }
          >
            {member.display_name || member.email}
          </span>
          {isSelf && <span className="pl-1 text-fg-muted-regular">(You)</span>}
          {member.display_name && member.email && (
            <span className="pl-2 text-fg-muted-regular-small">
              {member.email}
            </span>
          )}
        </span>
        {member.pending && <Badge label="Invited" className="shrink-0" />}
      </span>
      {member.pending ? (
        <TooltipButton
          title="Cancel invitation"
          aria-label={`Cancel invitation for ${member.email}`}
          variant="contextual"
          size="xs"
          className="shrink-0 opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
          onClick={onRemove}
        >
          <CloseIcon fontSize="inherit" />
        </TooltipButton>
      ) : (
        <>
          <button
            ref={anchorRef}
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className="menu-trigger-sm w-28 shrink-0 gap-1.5"
          >
            {roleLabel(member.role)}
            <UnfoldMoreIcon
              sx={{ fontSize: 12 }}
              className="shrink-0 text-fg-muted"
            />
          </button>
          <DropdownMenu
            anchorRef={anchorRef}
            isOpen={open}
            onClose={() => setOpen(false)}
            placement="bottom-end"
          >
            {roleChangeBlock && (
              <span className="block px-2 pt-1 pb-0.5 text-fg-muted-regular-small select-none">
                {roleChangeBlock}
              </span>
            )}
            {roleItems}
            <div className="mx-2 my-1 h-px bg-[var(--color-border)]" />
            {removeBlock ? (
              <Tooltip title={removeBlock}>
                <span className="block">
                  <span
                    aria-disabled
                    className="flex w-full cursor-not-allowed items-center gap-1.5 px-2 text-left whitespace-nowrap text-fg-muted-regular opacity-60 item-height"
                  >
                    <CloseIcon sx={{ fontSize: 12 }} />
                    Remove from organization
                  </span>
                </span>
              </Tooltip>
            ) : (
              <DropdownMenuItem
                onClick={() => {
                  setOpen(false);
                  onRemove();
                }}
              >
                <span className="flex flex-1 items-center gap-1.5 text-left whitespace-nowrap text-error">
                  <CloseIcon sx={{ fontSize: 12 }} />
                  Remove from organization
                </span>
              </DropdownMenuItem>
            )}
          </DropdownMenu>
        </>
      )}
    </div>
  );
};
